import { keyframes } from "goober";
import { useLayoutEffect, useState } from "react";

type NavKind = "chat" | "workspace" | "workflow" | "settings" | "admin";

// Each sidebar sits one level deeper than the last (back arrows walk left up
// the ladder), so entering a deeper one slides from the right and returning
// slides from the left.
const navDepth: Record<NavKind, number> = {
  chat: -1,
  workspace: 0,
  workflow: 1,
  settings: 2,
  admin: 3,
};

type NavSurface = "sidebar" | "mode-switch" | "header" | "body";
const lastNavKinds: Record<NavSurface, NavKind | null> = {
  sidebar: null,
  "mode-switch": null,
  header: null,
  body: null,
};

// Sidebars remount on every page switch, so a plain mount animation would
// replay constantly; slide only when the sidebar kind actually changes.
export function useNavTransition(
  kind: NavKind,
  surface: NavSurface = "sidebar"
): "left" | "right" | undefined {
  const [previous] = useState(() => lastNavKinds[surface]);
  useLayoutEffect(() => {
    lastNavKinds[surface] = kind;
  }, [kind, surface]);
  if (previous === null || previous === kind) return undefined;
  if (
    (previous === "chat" && kind === "workspace") ||
    (previous === "workspace" && kind === "chat")
  ) {
    return kind === "workspace" ? "right" : "left";
  }
  return navDepth[kind] > navDepth[previous] ? "right" : "left";
}

export const NAV_TRANSITION_DISTANCE = 12;

// Every 3B surface enters with the same short slide and fade; only the direction changes.
const slideIn = (x: number, y: number) => keyframes`
  from {
    opacity: 0;
    transform: translate(${x}px, ${y}px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const slideFromRight = slideIn(NAV_TRANSITION_DISTANCE, 0);
const slideFromLeft = slideIn(-NAV_TRANSITION_DISTANCE, 0);

export const riseInAnimation = `${slideIn(0, NAV_TRANSITION_DISTANCE)} var(--motion-base) var(--ease-out) both`;

export const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export const navEnterStyles = `
  &[data-nav-enter="right"] {
    animation: ${slideFromRight} var(--motion-base) var(--ease-out);
  }
  &[data-nav-enter="left"] {
    animation: ${slideFromLeft} var(--motion-base) var(--ease-out);
  }
`;
